import { getI18n, intlLocale } from "../i18n";

export function errorMessage(error: unknown, fallback: string): string {
	if (error instanceof Error && error.message) return error.message;
	return fallback;
}

/** Compact count: 1234 → 1.2k, 23456 → 2.3w. */
export function formatCount(value: number): string {
	if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}m`;
	if (value >= 10_000) return `${(value / 10_000).toFixed(1)}w`;
	if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
	return String(value);
}

// Intl formatter construction is expensive; these run per row in long lists
// (notifications, messages, wallet ledger), so cache one instance per locale.
const numberFormats = new Map<string, Intl.NumberFormat>();
const dateTimeFormats = new Map<string, Intl.DateTimeFormat>();
const relativeTimeFormats = new Map<string, Intl.RelativeTimeFormat>();

function cached<T>(cache: Map<string, T>, locale: string, create: (locale: string) => T): T {
	let formatter = cache.get(locale);
	if (!formatter) {
		formatter = create(locale);
		cache.set(locale, formatter);
	}
	return formatter;
}

/** Full localized number without compact notation. */
export function formatNumber(value: number): string {
	return cached(numberFormats, intlLocale(), (locale) => new Intl.NumberFormat(locale)).format(
		value,
	);
}

export function formatDateTime(value: string): string {
	try {
		return cached(
			dateTimeFormats,
			intlLocale(),
			(locale) =>
				new Intl.DateTimeFormat(locale, {
					month: "short",
					day: "numeric",
					hour: "2-digit",
					minute: "2-digit",
				}),
		).format(new Date(value));
	} catch {
		return value;
	}
}

/** "3分钟前" / "3 minutes ago" style relative time. */
export function relativeTime(value: string | number): string {
	const ts = typeof value === "number" ? value : new Date(value).getTime();
	if (Number.isNaN(ts)) return "";
	const diff = Date.now() - ts;
	const min = Math.floor(diff / 60_000);
	if (min < 1) return getI18n().t("common.justNow");
	const rtf = cached(
		relativeTimeFormats,
		intlLocale(),
		(locale) => new Intl.RelativeTimeFormat(locale, { numeric: "always" }),
	);
	if (min < 60) return rtf.format(-min, "minute");
	const hr = Math.floor(min / 60);
	if (hr < 24) return rtf.format(-hr, "hour");
	const day = Math.floor(hr / 24);
	if (day < 30) return rtf.format(-day, "day");
	return formatDateTime(new Date(ts).toISOString());
}

export function initialOf(name: string | null | undefined): string {
	const trimmed = (name ?? "").trim();
	if (!trimmed) return "M";
	return trimmed.slice(0, 1).toUpperCase();
}
